import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  CardBody,
  Input,
} from "@material-tailwind/react";
import PsychBlockedDatesPicker from "../components/admin/PsychBlockedDatesPicker";
import PsychologistPhotoSpecialtyBlock from "../components/admin/PsychologistPhotoSpecialtyBlock";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import WeeklyScheduleEditor from "../components/admin/WeeklyScheduleEditor";
import ConsultationModePicker from "../components/admin/ConsultationModePicker";
import MexicoLocationSelects, {
  locationToPayload,
} from "../components/admin/MexicoLocationSelects";
import { defaultWeeklyTemplate } from "../utils/psychSchedule";
import { todayMxYmd } from "../utils/dateMx";
import { CM_BOTH } from "../utils/consultationModes";

export default function PortalAdminNewPsych() {
  const { user, isAuthenticated } = usePortalAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [blockedYmd, setBlockedYmd] = useState([]);
  const [address, setAddress] = useState("");
  const [weekly, setWeekly] = useState(() => defaultWeeklyTemplate());
  const [hasUserLogin, setHasUserLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [consultationModes, setConsultationModes] = useState(CM_BOTH);
  const [loading, setLoading] = useState(false);
  const [specialtyDescription, setSpecialtyDescription] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const pendingPhotoRef = useRef(null);
  const [locationSel, setLocationSel] = useState({
    stateCode: "",
    municipalityCode: "",
  });

  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview]
  );

  function handlePhotoFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    pendingPhotoRef.current = file || null;
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (hasUserLogin && (!loginEmail?.trim() || !loginPassword || loginPassword.length < 8)) {
      toast.error("Si activas acceso, indica el correo y la contraseña (mín. 8 caracteres).");
      return;
    }
    const ef = todayMxYmd();
    const loc = locationToPayload(locationSel.stateCode, locationSel.municipalityCode);
    if (!loc) {
      toast.error("Selecciona estado y municipio o alcaldía de la consulta presencial.");
      return;
    }
    setLoading(true);
    const body = {
      name,
      email: email || undefined,
      phone: phone || undefined,
      specialtyDescription: specialtyDescription.trim() || undefined,
      consultationModes,
      location: loc,
      address: address.trim() || undefined,
      schedule: {
        blockedDates: blockedYmd,
        versions: [
          {
            effectiveFrom: ef,
            weekly,
          },
        ],
      },
      hasUserLogin,
      loginEmail: hasUserLogin ? loginEmail.trim() : undefined,
      loginPassword: hasUserLogin ? loginPassword : undefined,
    };
    try {
      const created = await portalApi.adminCreatePsychologist(body);
      const newId = created?.psychologist?.psychologistId;
      const file = pendingPhotoRef.current;
      if (newId && file) {
        setUploadingPhoto(true);
        const maxBytes = 5 * 1024 * 1024;
        if (file.size > maxBytes) {
          toast.error("La imagen supera 5 MB; el psicólogo se creó sin foto.");
        } else {
          const allowed = ["image/jpeg", "image/png", "image/webp"];
          const ct = allowed.includes(file.type) ? file.type : "image/jpeg";
          if (file.type && !allowed.includes(file.type)) {
            toast.error("Usa JPG, PNG o WebP; el psicólogo se creó sin foto.");
          } else {
            try {
              const r = await portalApi.adminPsychologistPhotoUploadUrl({
                psychologistId: newId,
                contentType: ct,
              });
              const putRes = await fetch(r.uploadUrl, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": r.contentType || ct },
              });
              if (!putRes.ok) throw new Error("upload");
              await portalApi.adminUpdatePsychologist({
                psychologistId: newId,
                profileImageKey: r.imageKey,
              });
            } catch {
              toast.error("El psicólogo se creó, pero no se pudo subir la foto. Edítalo desde el horario.");
            }
          }
        }
      }
      toast.success("Psicólogo creado correctamente");
      setTimeout(() => navigate("/admin/psicologos"), 800);
    } catch (err) {
      toast.error(err.message || "No se pudo guardar");
    } finally {
      setLoading(false);
      setUploadingPhoto(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    if (user?.role === "psychologist") {
      return <Navigate to="/psicologo" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <PortalShell wide>
      <form id="new-psych-form" onSubmit={onSubmit} className="mx-auto max-w-6xl pb-24">
        {/* Back link + page title — aligned with the cards */}
        <div className="mb-6">
          <Link to="/admin/psicologos" className="portal-meta font-medium text-brand-lavender hover:underline">
            ← Volver a administración
          </Link>
          <h1 className="mt-2 font-sans text-2xl sm:text-3xl font-semibold tracking-tight text-brand-dark">
            Nuevo psicólogo
          </h1>
          <p className="mt-1 text-brand-gray text-sm sm:text-base max-w-xl leading-relaxed">
            Datos de contacto, perfil, ubicación y horario.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Columna izquierda: perfil */}
          <div className="space-y-5">
            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-4">
                <h2 className="portal-section-title">Datos del psicólogo</h2>
                <Input
                  label="Nombre completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  label="Correo (opcional)"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  label="Teléfono (opcional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  size="lg"
                  className="!text-brand-dark"
                />
              </CardBody>
            </Card>

            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-3">
                <h2 className="portal-section-title">Foto y especialidad</h2>
                <PsychologistPhotoSpecialtyBlock
                  idPrefix="admin-new"
                  profileImageUrl={null}
                  localPreviewUrl={photoPreview}
                  specialtyDescription={specialtyDescription}
                  onSpecialtyChange={setSpecialtyDescription}
                  onFileChange={handlePhotoFileChange}
                  uploadingPhoto={uploadingPhoto}
                  disabled={loading}
                />
              </CardBody>
            </Card>

            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-3">
                <h2 className="portal-section-title">Modalidad de consulta</h2>
                <p className="portal-meta">
                  Define si los pacientes podrán elegir presencial, virtual o ambas al agendar.
                </p>
                <ConsultationModePicker
                  value={consultationModes}
                  onChange={setConsultationModes}
                  disabled={loading}
                />
              </CardBody>
            </Card>

            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-3">
                <h2 className="portal-section-title">Ubicación presencial (México)</h2>
                <p className="portal-meta">Catálogo INEGI (estado + municipio o alcaldía).</p>
                <MexicoLocationSelects value={locationSel} onChange={setLocationSel} />
                <div className="space-y-1.5 mt-1">
                  <label className="portal-label">Dirección</label>
                  <textarea
                    rows={3}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Calle, número, colonia, referencias…"
                    className="w-full rounded-xl border border-blue-gray-200 bg-white px-3.5 py-2.5 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/60 focus:border-brand-lavender focus:outline-none focus:ring-2 focus:ring-brand-lavender/20 resize-none transition-colors"
                  />
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Columna derecha: agenda + acceso */}
          <div className="space-y-5">
            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-4">
                <h2 className="portal-section-title">Horario</h2>
                <WeeklyScheduleEditor weekly={weekly} onChange={setWeekly} />
              </CardBody>
            </Card>

            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-3">
                <h2 className="portal-section-title">Días no disponibles</h2>
                <PsychBlockedDatesPicker blockedYmd={blockedYmd} onChange={setBlockedYmd} />
              </CardBody>
            </Card>

            <Card className="border border-brand-brown/15">
              <CardBody className="p-5 sm:p-6 space-y-3">
                <h2 className="portal-section-title">Acceso al portal</h2>
                <p className="portal-meta">
                  Opcional: permite que el psicólogo entre con su propio correo y contraseña.
                </p>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasUserLogin}
                    onChange={(e) => setHasUserLogin(e.target.checked)}
                    className="h-4 w-4 rounded border-brand-brown/40 text-brand-lavender focus:ring-brand-lavender"
                  />
                  <span className="portal-value">Crear usuario de acceso para este psicólogo</span>
                </label>
                {hasUserLogin && (
                  <div className="space-y-4 border-l-2 border-brand-lavender/30 pl-4">
                    <Input
                      type="email"
                      label="Correo de acceso"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      size="lg"
                      className="!text-brand-dark"
                    />
                    <Input
                      type="password"
                      label="Contraseña inicial (mín. 8 caracteres)"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      minLength={8}
                      size="lg"
                      className="!text-brand-dark"
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Barra de acciones fija */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-brand-lavender/20 bg-white/95 backdrop-blur-md shadow-[0_-2px_12px_rgba(65,68,109,0.08)]">
          <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 px-4 py-3">
            <Button
              type="button"
              variant="text"
              className="text-brand-dark normal-case"
              onClick={() => navigate("/admin/psicologos")}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-brand-lavender normal-case shadow-md shadow-brand-lavender/25"
              disabled={loading || uploadingPhoto}
            >
              {loading || uploadingPhoto ? "Guardando…" : "Guardar psicólogo"}
            </Button>
          </div>
        </div>
        </form>
    </PortalShell>
  );
}
